/**
 * AIScoutingSummaryCardContainer - Container for AI scouting summary card
 *
 * Manages AI summary generation for scouting intelligence (scout reports + notes).
 * Uses a dedicated scouting prompt that focuses exclusively on reports and notes
 * rather than metrics/valuation data. Disables generation when no scouting
 * intelligence is available.
 *
 * Only includes scout reports the current user authored (canEdit === true) in the
 * AI prompt. Reports from other scouts that haven't been shared with the user are
 * excluded to respect access boundaries.
 * @remarks This is a Container component - handles all business logic
 */

import { useMemo } from "react";

import {
  PlayerDetailPlayerMetaFragment,
  useNotesListNotesByPlayerIdQuery,
} from "@/generated/graphql";

import { useAISummaryCard } from "@/features/player-detail/hooks/useAISummaryCard";
import AIScoutingSummaryCardView from "./AIScoutingSummaryCardView";

/** Message shown when no scouting intelligence exists */
const NO_INTELLIGENCE_MESSAGE =
  "No scouting intelligence available. Add scouting reports or notes to generate a summary.";

/**
 * Props for AIScoutingSummaryCardContainer component
 */
interface AIScoutingSummaryCardContainerProps {
  /** Player data fragment */
  player: PlayerDetailPlayerMetaFragment | null | undefined;
}

/**
 * AIScoutingSummaryCardContainer handles scouting summary generation and state.
 * Fetches notes independently and passes them to the scouting prompt generator.
 * @param props - Container props
 * @param props.player - Player data fragment
 * @returns AIScoutingSummaryCardView with computed props
 */
const AIScoutingSummaryCardContainer = ({
  player,
}: AIScoutingSummaryCardContainerProps) => {
  const { data: notesData } = useNotesListNotesByPlayerIdQuery({
    variables: { playerId: player?.id ?? "" },
    skip: !player?.id,
  });

  const accessiblePlayer = useMemo(
    () =>
      player
        ? {
            ...player,
            scoutReports:
              player.scoutReports?.filter(r => r?.canEdit === true) ?? [],
          }
        : player,
    [player]
  );

  const hasScoutingIntelligence = useMemo(() => {
    const hasReports = (accessiblePlayer?.scoutReports?.length ?? 0) > 0;
    const hasNotes = (notesData?.listNotesByPlayerId?.edges?.length ?? 0) > 0;
    return hasReports || hasNotes;
  }, [
    accessiblePlayer?.scoutReports?.length,
    notesData?.listNotesByPlayerId?.edges?.length,
  ]);

  const isGenerateDisabled = !hasScoutingIntelligence;

  const {
    isDark,
    isModalOpen,
    displayContent,
    fullContent,
    isGenerating,
    error,
    lastUpdated,
    showReadMore,
    pickerLocale,
    saveAsDefault,
    selectedLanguageLabel,
    supportedLanguages,
    handleReadMore,
    handleModalClose,
    handleCopySummary,
    onSelectLanguage,
    isButtonDisabled,
  } = useAISummaryCard({
    player: accessiblePlayer,
    summaryType: "scouting",
    notesData,
    isGenerateDisabled,
  });

  return (
    <AIScoutingSummaryCardView
      isDark={isDark}
      summaryContent={displayContent}
      fullSummaryContent={fullContent}
      lastGenerated={lastUpdated}
      isGenerating={isGenerating}
      hasError={!!error}
      errorMessage={error}
      showReadMore={showReadMore}
      isModalOpen={isModalOpen}
      selectedLanguageLabel={selectedLanguageLabel}
      supportedLanguages={[...supportedLanguages]}
      selectedLanguage={pickerLocale}
      saveAsDefault={saveAsDefault}
      isGenerateDisabled={isGenerateDisabled}
      disabledMessage={NO_INTELLIGENCE_MESSAGE}
      onReadMore={handleReadMore}
      onModalClose={handleModalClose}
      onSelectLanguage={onSelectLanguage}
      isButtonDisabled={isButtonDisabled}
      onCopySummary={handleCopySummary}
    />
  );
};

AIScoutingSummaryCardContainer.displayName = "AIScoutingSummaryCardContainer";

export default AIScoutingSummaryCardContainer;
